<template>
  <div>
    <el-upload
      :action="uploadUrl"
      :before-upload="handleBeforeUpload"
      :on-success="handleUploadSuccess"
      :on-error="handleUploadError"
      name="file"
      :show-file-list="false"
      :headers="headers"
      class="editor-img-uploader"
      v-if="type == 'url'"
    >
      <i ref="uploadRef" class="editor-img-uploader"></i>
    </el-upload>
  </div>
  <div class="editor" :style="editorContainerStyle">
    <quill-editor
      ref="quillEditorRef"
      v-model:content="content"
      contentType="html"
      @textChange="handleTextChange"
      :options="options"
      :style="styles"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, toRaw, getCurrentInstance } from 'vue';
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { getToken } from "@/utils/auth";

const { proxy } = getCurrentInstance();

const quillEditorRef = ref();
const uploadUrl = ref(import.meta.env.VITE_APP_BASE_API + "/file/upload"); // 上传的图片服务器地址
const headers = ref({
  Authorization: "Bearer " + getToken()
});

// 定义 emit
const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  /* 编辑器的内容 */
  modelValue: {
    type: String,
  },
  /* 高度 */
  height: {
    type: Number,
    default: null,
  },
  /* 最小高度 */
  minHeight: {
    type: Number,
    default: null,
  },
  /* 最大高度 */
  maxHeight: {
    type: Number,
    default: null,
  },
  /* 只读 */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /* 上传文件大小限制(MB) */
  fileSize: {
    type: Number,
    default: 5,
  },
  /* 类型（base64格式、url格式） */
  type: {
    type: String,
    default: "url",
  },
  /* 是否禁用图片和视频上传 */
  disableImageVideo: {
    type: Boolean,
    default: false,
  }
});

const options = computed(() => {
  // 基础工具栏配置
  const baseToolbar = [
    ["bold", "italic", "underline", "strike"],      // 加粗 斜体 下划线 删除线
    ["blockquote", "code-block"],                   // 引用  代码块
    [{ list: "ordered" }, { list: "bullet" }],      // 有序、无序列表
    [{ indent: "-1" }, { indent: "+1" }],           // 缩进
    [{ size: ["small", false, "large", "huge"] }],  // 字体大小
    [{ header: [1, 2, 3, 4, 5, 6, false] }],        // 标题
    [{ color: [] }, { background: [] }],            // 字体颜色、字体背景颜色
    [{ align: [] }],                                // 对齐方式
    ["clean"],                                      // 清除文本格式
  ];
  
  // 根据 disableImageVideo prop 决定是否添加图片和视频
  if (props.disableImageVideo) {
    baseToolbar.push(["link"]); // 只添加链接
  } else {
    baseToolbar.push(["link", "image", "video"]); // 添加链接、图片、视频
  }
  
  return {
    theme: "snow",
    bounds: document.body,
    debug: "warn",
    modules: {
      // 工具栏配置
      toolbar: baseToolbar,
    },
    placeholder: "请输入内容",
    readOnly: props.readOnly
  };
});

const styles = computed(() => {
  let style = {};
  if (props.minHeight) {
    style.minHeight = `${props.minHeight}px`;
  }
  if (props.height) {
    style.height = `${props.height}px`;
  }
  return style;
});

// 编辑器容器样式，用于控制最大高度和滚动
const editorContainerStyle = computed(() => {
  let style = {};
  if (props.maxHeight) {
    style.maxHeight = `${props.maxHeight}px`;
    style.overflowY = 'auto';
  }
  return style;
});

const content = ref("");
let isUpdating = false; // 防止循环更新

// 处理文本变化事件
function handleTextChange() {
  if (!isUpdating) {
    emit('update:modelValue', content.value);
  }
}

watch(() => props.modelValue, (v) => {
  // 确保值总是更新，即使内容相同也要更新（用于强制刷新）
  const newValue = v == undefined || v === null || v === '' ? "<p></p>" : v;
  if (content.value !== newValue) {
    isUpdating = true;
    content.value = newValue;
    // 如果 QuillEditor 已经初始化，通过 API 设置内容
    nextTick(() => {
      if (quillEditorRef.value && quillEditorRef.value.getQuill) {
        try {
          const quill = quillEditorRef.value.getQuill();
          const currentContent = quill.root.innerHTML;
          if (currentContent !== newValue) {
            quill.clipboard.dangerouslyPasteHTML(newValue);
          }
        } catch (e) {
          console.warn('QuillEditor update error:', e);
        }
      }
      isUpdating = false;
    });
  }
}, { immediate: true, deep: true });

// 如果设置了上传地址且未禁用图片视频，则自定义图片上传事件
onMounted(() => {
  if (props.type == 'url' && !props.disableImageVideo) {
    let quill = quillEditorRef.value.getQuill();
    let toolbar = quill.getModule("toolbar");
    toolbar.addHandler("image", (value) => {
      if (value) {
        proxy.$refs.uploadRef.click();
      } else {
        quill.format("image", false);
      }
    });
  }
});

// 上传前校检格式和大小
function handleBeforeUpload(file) {
  const type = ["image/jpeg", "image/jpg", "image/png", "image/svg"];
  const isJPG = type.includes(file.type);
  //检验文件格式
  if (!isJPG) {
    proxy.$modal.msgError(`图片格式错误!`);
    return false;
  }
  // 校检文件大小
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      proxy.$modal.msgError(`上传文件大小不能超过 ${props.fileSize} MB!`);
      return false;
    }
  }
  return true;
}

// 上传成功处理
function handleUploadSuccess(res, file) {
  // 如果上传成功
  if (res.code == 200) {
    // 获取富文本实例
    let quill = toRaw(quillEditorRef.value).getQuill();
    // 获取光标位置
    let length = quill.selection.savedRange.index;
    // 插入图片，res.url为服务器返回的图片链接地址
    quill.insertEmbed(length, "image", res.data.url);
    // 调整光标到最后
    quill.setSelection(length + 1);
  } else {
    proxy.$modal.msgError("图片插入失败");
  }
}

// 上传失败处理
function handleUploadError() {
  proxy.$modal.msgError("图片插入失败");
}
</script>

<style>
.editor-img-uploader {
  display: none;
}
.editor, .ql-toolbar {
  white-space: pre-wrap !important;
  line-height: normal !important;
}

/* 当设置了 maxHeight 时，确保编辑器内容区域可以滚动 */
.editor :deep(.ql-container) {
  max-height: inherit;
  overflow-y: auto;
}

.editor :deep(.ql-editor) {
  overflow-y: visible;
  min-height: inherit;
}
.quill-img {
  display: none;
}
.ql-snow .ql-tooltip[data-mode="link"]::before {
  content: "请输入链接地址:";
}
.ql-snow .ql-tooltip.ql-editing a.ql-action::after {
  border-right: 0px;
  content: "保存";
  padding-right: 0px;
}
.ql-snow .ql-tooltip[data-mode="video"]::before {
  content: "请输入视频地址:";
}
.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before {
  content: "14px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before {
  content: "10px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before {
  content: "18px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before {
  content: "32px";
}
.ql-snow .ql-picker.ql-header .ql-picker-label::before,
.ql-snow .ql-picker.ql-header .ql-picker-item::before {
  content: "文本";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before {
  content: "标题1";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
  content: "标题2";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
  content: "标题3";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before {
  content: "标题4";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before {
  content: "标题5";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before {
  content: "标题6";
}
.ql-snow .ql-picker.ql-font .ql-picker-label::before,
.ql-snow .ql-picker.ql-font .ql-picker-item::before {
  content: "标准字体";
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="serif"]::before {
  content: "衬线字体";
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before {
  content: "等宽字体";
}
</style>
